/**
 * DOM element → paginated PDF export.
 *
 * No prior jsPDF/html2canvas usage existed anywhere else in this codebase
 * (checked before writing this — see InvoiceCreateManual.vue's "Download
 * Invoice" button, the first consumer), so this is a fresh, small, reusable
 * helper rather than a one-off inline implementation, in case any other
 * screen wants a "download this as PDF" button later.
 *
 * Renders the element to a single large canvas via html2canvas, then slices
 * that canvas into page-height chunks and adds each as its own A4 PDF page
 * — this is the standard html2canvas+jsPDF pagination technique. It avoids
 * jamming a tall document (like a multi-page-worthy invoice with many line
 * items) onto one squished page. Note: because it slices by fixed pixel
 * height rather than being aware of the underlying DOM's row boundaries, a
 * table row that happens to straddle a page-height boundary can still be
 * split across two pages — same limitation most canvas-based PDF exporters
 * have. Good enough for an invoice preview download; a pixel-perfect,
 * row-aware paginator would need a very different (print-CSS / server-side)
 * approach.
 */
// jsPDF pulls in `fast-png` (PNG decoding support), which ships raw ES
// class-field syntax (`_checkCrc;` etc.) — webpack's default config here
// only runs babel-loader on first-party src/ code, not node_modules, so it
// chokes trying to parse that syntax directly. Fixed via
// `transpileDependencies: ['jspdf', 'fast-png', 'iobuffer']` in
// vue.config.js, which tells vue-cli to also run babel-loader over these
// specific packages (babel-preset-app already ships
// @babel/plugin-proposal-class-properties, so once it actually runs on
// these files, the class fields transpile down fine).
import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import {
  INVOICE_MAX_FILE_SIZE_BYTES,
  INVOICE_FILE_TOO_LARGE_MESSAGE,
} from '@/utils/invoice-filename'

const A4_WIDTH_MM = 210
const A4_HEIGHT_MM = 297

// JPEG quality ladder when the first PDF blob exceeds the size cap.
// Prefer quality reduction over re-rasterizing whenever possible.
const PDF_SIZE_RETRY_QUALITIES = [0.92, 0.82, 0.72, 0.6, 0.5]

// A typical invoice (a handful of line items, standard field set) should
// come out as ONE A4 page, matching the real reference invoice this app's
// layout was designed against — see GenericInvoicePreview.vue. Rather than
// always slicing 1:1 into as many page-height chunks as it takes (which
// turned any content that was even slightly taller than one page into an
// awkward near-empty second page), content that overflows by a modest
// amount is instead shrunk down (preserving aspect ratio, so nothing gets
// visually distorted) to fit entirely on one page. This only kicks in up
// to MIN_SINGLE_PAGE_SCALE — beyond that the shrink would make the text
// uncomfortably small, so genuinely long invoices (e.g. 30+ line items)
// still fall back to the original multi-page slicing instead of forcing
// everything onto one illegibly tiny page.
//
// 2026-07-23: raised from 0.62 → 0.82 so scale-to-fit never crushes body
// text below a readable print size. Prefer a second A4 page over ~60% text.
const MIN_SINGLE_PAGE_SCALE = 0.82

// Waits a couple of animation frames so any pending Vue reactive re-render
// has actually painted before html2canvas reads the DOM — a click handler
// firing synchronously can otherwise race ahead of a template update that
// was only just triggered (e.g. a v-if just flipped true this same tick).
function waitForNextPaint() {
  return new Promise(resolve => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
}

// Waits for every <img> inside the target to finish loading (success or
// failure) so html2canvas never captures a half-loaded logo/signature
// image. Bounded by `timeoutMs` so one slow/broken image can't hang the
// whole export — proceeds anyway once the timeout elapses.
function waitForImages(element, timeoutMs = 4000) {
  const images = Array.from(element.querySelectorAll('img'))
  const pending = images.filter(img => !img.complete)
  if (!pending.length) return Promise.resolve()

  return Promise.race([
    Promise.all(pending.map(img => new Promise(resolve => {
      img.addEventListener('load', resolve, { once: true })
      img.addEventListener('error', resolve, { once: true })
    }))),
    new Promise(resolve => setTimeout(resolve, timeoutMs)),
  ])
}

// When the live preview sits under `display:none` (e.g. Bootstrap
// `d-none d-lg-block` on viewports < lg) or a closed modal, offsetWidth /
// offsetHeight are 0 and html2canvas would rasterize a blank page. Clone
// into an off-screen but *laid-out* host with explicit A4 width so capture
// still works. Vue scoped `data-v-*` attrs are preserved on the clone, so
// invoice styles still apply. Prefer the live node when it is measurable.
function mountSizedExportClone(sourceEl) {
  const host = document.createElement('div')
  host.setAttribute('data-pdf-export-host', '1')
  Object.assign(host.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '210mm',
    maxWidth: '210mm',
    margin: '0',
    padding: '0',
    border: '0',
    // Keep in the layout tree (not display:none / visibility:hidden).
    opacity: '0',
    pointerEvents: 'none',
    zIndex: '-1',
    overflow: 'visible',
  })
  const clone = sourceEl.cloneNode(true)
  clone.style.transform = 'none'
  clone.style.webkitTransform = 'none'
  clone.style.width = '210mm'
  clone.style.maxWidth = '210mm'
  clone.style.minHeight = clone.style.minHeight || '297mm'
  clone.style.margin = '0'
  host.appendChild(clone)
  document.body.appendChild(host)
  return {
    element: clone,
    cleanup() {
      if (host.parentNode) host.parentNode.removeChild(host)
    },
  }
}

async function resolveCaptureTarget(element) {
  await waitForNextPaint()
  await waitForImages(element)

  if (element.offsetWidth > 0 && element.offsetHeight > 0) {
    return { element, cleanup() {} }
  }

  const mounted = mountSizedExportClone(element)
  await waitForNextPaint()
  await waitForImages(mounted.element)

  if (mounted.element.offsetWidth === 0 || mounted.element.offsetHeight === 0) {
    mounted.cleanup()
    throw new Error('exportElementToPdf: target element has zero size (is it hidden or not yet rendered?)')
  }
  return mounted
}

// ── Preview/PDF fidelity guard ──────────────────────────────────────────
// The whole point of this module is "what's on screen is what gets
// downloaded" — the target element is captured exactly as laid out, not a
// separate hidden clone. But an *ancestor* of the target (e.g. a sticky
// live-preview panel with its own independent `overflow-y: auto` scroll
// region, sized in vh so a long form column doesn't push it off-screen) or
// a *descendant* inside the target (e.g. a table wrapped in a scrollable
// responsive div) can each have `overflow: auto|scroll|hidden` with a
// height/width smaller than their actual content. On screen that's totally
// fine — the user just scrolls to see the rest — but html2canvas rasterizes
// the DOM as laid out, so anything past that clipped boundary would
// silently vanish from the exported PDF even though the user never
// noticed it was cut off (they'd have had to scroll to find out).
//
// This finds every such clipping container in the target's ancestor chain
// AND its own descendant subtree, temporarily relaxes it so the full
// content lays out and gets captured, then restores every inline style it
// touched immediately after — the live page's actual scroll behavior is
// completely unaffected once the capture finishes. (The target element
// itself is deliberately left alone — GenericInvoicePreview.vue's own
// `overflow: hidden` on its root is an intentional rounded-corner/watermark
// containment, not a bug to "fix".)
function findClippingContainers(element) {
  const candidates = []
  let ancestor = element.parentElement
  while (ancestor && ancestor !== document.documentElement) {
    candidates.push(ancestor)
    ancestor = ancestor.parentElement
  }
  candidates.push(...element.querySelectorAll('*'))

  return candidates.filter((node) => {
    const style = window.getComputedStyle(node)
    const clipsY = /^(auto|scroll|hidden)$/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1
    const clipsX = /^(auto|scroll|hidden)$/.test(style.overflowX) && node.scrollWidth > node.clientWidth + 1
    return clipsY || clipsX
  })
}

function relaxClipping(nodes) {
  return nodes.map((node) => {
    const original = {
      overflow: node.style.overflow,
      overflowX: node.style.overflowX,
      overflowY: node.style.overflowY,
      maxHeight: node.style.maxHeight,
      maxWidth: node.style.maxWidth,
    }
    node.style.overflow = 'visible'
    node.style.overflowX = 'visible'
    node.style.overflowY = 'visible'
    node.style.maxHeight = 'none'
    node.style.maxWidth = 'none'
    return { node, original }
  })
}

function restoreClipping(relaxed) {
  relaxed.forEach(({ node, original }) => {
    node.style.overflow = original.overflow
    node.style.overflowX = original.overflowX
    node.style.overflowY = original.overflowY
    node.style.maxHeight = original.maxHeight
    node.style.maxWidth = original.maxWidth
  })
}

// Side-panel / modal preview wrappers use `transform: scale(...)` so a true
// A4 sheet fits the available width without reflowing layout. html2canvas
// applies those ancestor transforms to the capture (blurry / wrong size),
// so briefly clear them for the rasterize pass — same restore pattern as
// the clipping guard above.
function findTransformedAncestors(element) {
  const nodes = []
  let ancestor = element.parentElement
  while (ancestor && ancestor !== document.documentElement) {
    const style = window.getComputedStyle(ancestor)
    if (style.transform && style.transform !== 'none') {
      nodes.push(ancestor)
    }
    ancestor = ancestor.parentElement
  }
  return nodes
}

function relaxTransforms(nodes) {
  return nodes.map((node) => {
    const original = {
      transform: node.style.transform,
      webkitTransform: node.style.webkitTransform,
    }
    node.style.transform = 'none'
    node.style.webkitTransform = 'none'
    return { node, original }
  })
}

function restoreTransforms(relaxed) {
  relaxed.forEach(({ node, original }) => {
    node.style.transform = original.transform
    node.style.webkitTransform = original.webkitTransform
  })
}

function canvasToImageData(canvas, imageType, quality) {
  if (imageType === 'JPEG') {
    return {
      dataUrl: canvas.toDataURL('image/jpeg', quality),
      format: 'JPEG',
    }
  }
  return {
    dataUrl: canvas.toDataURL('image/png'),
    format: 'PNG',
  }
}

function buildPdfFromCanvas(canvas, {
  margin = 0,
  backgroundColor = '#ffffff',
  imageType = 'JPEG',
  quality = 0.92,
} = {}) {
  const pdf = new jsPDF('p', 'mm', 'a4')
  const usableWidthMm = A4_WIDTH_MM - margin * 2
  const usableHeightMm = A4_HEIGHT_MM - margin * 2

  // px-per-mm once the full-width canvas is scaled down to fit usableWidthMm.
  const pxPerMm = canvas.width / usableWidthMm
  const pageHeightPx = Math.floor(usableHeightMm * pxPerMm)
  const contentHeightMm = canvas.height / pxPerMm

  // Already fits on one page at full size (pageHeightPx >= canvas.height) —
  // or is only slightly over and can be shrunk to fit without the text
  // becoming too small — draw it as a single, proportionally-scaled page
  // instead of slicing into multiple pages. See MIN_SINGLE_PAGE_SCALE above.
  const singlePageScale = usableHeightMm / contentHeightMm
  if (singlePageScale >= MIN_SINGLE_PAGE_SCALE) {
    const { dataUrl, format } = canvasToImageData(canvas, imageType, quality)
    const renderScale = Math.min(1, singlePageScale)
    const renderedWidthMm = usableWidthMm * renderScale
    const renderedHeightMm = contentHeightMm * renderScale
    // Centered horizontally when scaled down; keep top-aligned vertically.
    const xOffsetMm = margin + (usableWidthMm - renderedWidthMm) / 2
    pdf.addImage(dataUrl, format, xOffsetMm, margin, renderedWidthMm, renderedHeightMm)
    return pdf
  }

  // Fallback: genuinely too much content to fit on one page at a legible
  // size (e.g. 30+ line items) — slice into page-height chunks, one PDF
  // page each, same technique as before.
  let renderedPx = 0
  let pageIndex = 0

  while (renderedPx < canvas.height) {
    const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedPx)

    const pageCanvas = document.createElement('canvas')
    pageCanvas.width = canvas.width
    pageCanvas.height = sliceHeightPx
    const ctx = pageCanvas.getContext('2d')
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
    ctx.drawImage(
      canvas,
      0, renderedPx, canvas.width, sliceHeightPx,
      0, 0, canvas.width, sliceHeightPx,
    )

    const { dataUrl, format } = canvasToImageData(pageCanvas, imageType, quality)
    const sliceHeightMm = sliceHeightPx / pxPerMm

    if (pageIndex > 0) pdf.addPage()
    pdf.addImage(dataUrl, format, margin, margin, usableWidthMm, sliceHeightMm)

    renderedPx += sliceHeightPx
    pageIndex += 1
  }

  return pdf
}

async function captureElementCanvas(element, { scale, backgroundColor }) {
  // Prefer the live node; if it has zero size (hidden tab / d-none / closed
  // modal), fall back to an off-screen A4-sized clone — see resolveCaptureTarget.
  const mounted = await resolveCaptureTarget(element)
  const target = mounted.element

  // See findClippingContainers()'s comment above — briefly un-clip any
  // scrollable ancestor/descendant so nothing that's part of the document
  // but currently scrolled out of view gets silently cut off in the PDF.
  // Also drop preview fit-scale transforms so we capture the true A4 sheet.
  const relaxedClip = relaxClipping(findClippingContainers(target))
  const relaxedTx = relaxTransforms(findTransformedAncestors(target))
  // Let the relaxed layout actually reflow before html2canvas reads it.
  await waitForNextPaint()

  let canvas
  try {
    canvas = await html2canvas(target, {
      scale,
      useCORS: true,
      backgroundColor,
      logging: false,
      // Capture the element's laid-out box (true A4 width), not a
      // transform-scaled on-screen bounding rect.
      scrollX: 0,
      scrollY: 0,
    })
  } finally {
    restoreTransforms(relaxedTx)
    restoreClipping(relaxedClip)
    mounted.cleanup()
  }

  if (canvas.width === 0 || canvas.height === 0) {
    throw new Error('exportElementToPdf: html2canvas produced an empty canvas')
  }
  return canvas
}

function finalizePdfExport(pdf, filename, { download, returnBlob }) {
  const blob = pdf.output('blob')
  if (download) pdf.save(filename)
  if (returnBlob) {
    return { pdf, blob, filename }
  }
  return pdf
}

export async function exportElementToPdf(element, filename = 'document.pdf', options = {}) {
  if (!element) throw new Error('exportElementToPdf: no element provided')

  const {
    // 0 margin: GenericInvoicePreview is already a full A4 sheet with its
    // own internal padding — extra PDF margins would shrink readable type.
    margin = 0,
    // 2.5× device pixels keeps 11pt body type sharp on A4 raster export.
    scale = 2.5,
    backgroundColor = '#ffffff',
    // When true (default), trigger a browser download via pdf.save().
    download = true,
    // When true, also return { pdf, blob, filename } so callers can POST
    // the same bytes to the backend (e.g. persist generated invoice PDF).
    returnBlob = false,
    // Invoice PDFs must stay within this size (default 2 MB).
    maxFileSizeBytes = INVOICE_MAX_FILE_SIZE_BYTES,
    enforceMaxFileSize = true,
  } = options

  // Capture once at full quality scale, then compress via JPEG quality retries.
  let canvas = await captureElementCanvas(element, { scale, backgroundColor })
  const buildOptsBase = { margin, backgroundColor }

  const attempts = PDF_SIZE_RETRY_QUALITIES.map((quality) => ({
    imageType: 'JPEG',
    quality,
  }))

  for (let i = 0; i < attempts.length; i += 1) {
    const attempt = attempts[i]
    const pdf = buildPdfFromCanvas(canvas, { ...buildOptsBase, ...attempt })
    const blob = pdf.output('blob')
    if (!enforceMaxFileSize || blob.size <= maxFileSizeBytes) {
      return finalizePdfExport(pdf, filename, { download, returnBlob })
    }
  }

  // Still over limit — re-rasterize at a lower scale and retry quality ladder.
  if (scale > 1.6) {
    canvas = await captureElementCanvas(element, { scale: 1.6, backgroundColor })
    for (let i = 0; i < attempts.length; i += 1) {
      const attempt = attempts[i]
      const pdf = buildPdfFromCanvas(canvas, { ...buildOptsBase, ...attempt })
      const blob = pdf.output('blob')
      if (!enforceMaxFileSize || blob.size <= maxFileSizeBytes) {
        return finalizePdfExport(pdf, filename, { download, returnBlob })
      }
    }
  }

  throw new Error(INVOICE_FILE_TOO_LARGE_MESSAGE)
}
